import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Loader2, GripVertical, Trash2, Plane, Type, Image as ImageIcon, Minus, IdCard,
  Plus, Save, FileDown, Code, MessageCircle, Share2, Upload, Eye,
  Bed, Car, ShieldCheck, ArrowRightLeft, Ticket, StickyNote, CalendarClock,
  Users, Receipt, Ship,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { AirlineAutocomplete, AirportAutocomplete } from "../components/TravelAutocomplete";
import { DatePickerField, TimePickerField } from "../components/FlightDateTime";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const uid = () => Math.random().toString(36).slice(2, 10);

const BLOCK_LABELS = {
  flight_segment: { icon: Plane, label: "Flight" },
  hotel: { icon: Bed, label: "Hotel" },
  car_hire: { icon: Car, label: "Car Hire" },
  insurance: { icon: ShieldCheck, label: "Insurance" },
  transfer: { icon: ArrowRightLeft, label: "Transfer" },
  tour: { icon: Ticket, label: "Tour / Activity" },
  cruise: { icon: Ship, label: "Cruise" },
  note: { icon: StickyNote, label: "Terms and Conditions" },
  passengers: { icon: Users, label: "Passengers" },
  billing: { icon: Receipt, label: "Billing" },
  text: { icon: Type, label: "Text" },
  image: { icon: ImageIcon, label: "Image" },
  divider: { icon: Minus, label: "Divider" },
  agent_contact: { icon: IdCard, label: "Agent card" },
};

const ITEM_TYPES = ["hotel", "insurance", "transfer", "tour", "cruise", "note"];
const SECTION_TYPES = ["passengers", "billing"];
const LAYOUT_TYPES = ["text", "image", "divider", "agent_contact"];

const DISPLAY_TOGGLES = [];

const DEFAULT_TOGGLES = {};

const FORM_FIELDS = {
  hotel: [
    { k: "name", l: "Hotel name" }, { k: "address", l: "Address" },
    { k: "check_in", l: "Check-in date", t: "date" }, { k: "check_in_time", l: "Check-in time", t: "time" },
    { k: "check_out", l: "Check-out date", t: "date" }, { k: "check_out_time", l: "Check-out time", t: "time" },
    { k: "room_type", l: "Room type" }, { k: "meal_plan", l: "Meal plan" },
    { k: "guests", l: "Guests" }, { k: "phone", l: "Hotel phone" },
    { k: "confirmation", l: "Confirmation #" },
    { k: "image", l: "Room image", t: "image" },
    { k: "notes", l: "Notes", t: "textarea" },
  ],
  car_hire: [
    { k: "company", l: "Company" },
    { k: "pickup_location", l: "Pick-up location" }, { k: "pickup_date", l: "Pick-up date", t: "date" },
    { k: "dropoff_location", l: "Drop-off location" }, { k: "dropoff_date", l: "Drop-off date", t: "date" },
    { k: "confirmation", l: "Confirmation #" }, { k: "notes", l: "Notes", t: "textarea" },
  ],
  insurance: [
    { k: "provider", l: "Provider" }, { k: "policy_number", l: "Policy number" },
    { k: "coverage", l: "Coverage" },
    { k: "start_date", l: "Start date", t: "date" }, { k: "end_date", l: "End date", t: "date" },
    { k: "notes", l: "Notes", t: "textarea" },
  ],
  transfer: [
    { k: "company", l: "Company / description" },
    { k: "from_location", l: "Pickup from" }, { k: "to_location", l: "Drop-off to" },
    { k: "date", l: "Pickup date", t: "date" }, { k: "time", l: "Pickup time", t: "time" },
    { k: "car_type", l: "Car type" }, { k: "car_model", l: "Car model" },
    { k: "driver_phone", l: "Driver phone" },
    { k: "confirmation", l: "Confirmation #" },
    { k: "image", l: "Vehicle image", t: "image" },
    { k: "notes", l: "Notes", t: "textarea" },
  ],
  tour: [
    { k: "title", l: "Title" }, { k: "location", l: "Location" },
    { k: "date", l: "Date", t: "date" }, { k: "time", l: "Time", t: "time" },
    { k: "passengers", l: "Passengers", t: "number" },
    { k: "duration", l: "Duration" }, { k: "confirmation", l: "Confirmation #" },
    { k: "image", l: "Activity image", t: "image" },
    { k: "notes", l: "Notes", t: "textarea" },
  ],
  note: [
    { k: "title", l: "Title" }, { k: "date", l: "Date (optional)", t: "date" },
    { k: "body", l: "Note", t: "textarea" },
  ],
  cruise: [
    { k: "ship_name", l: "Ship name" }, { k: "cruise_line", l: "Cruise line" },
    { k: "port", l: "Departure port" },
    { k: "sailing_start", l: "Sailing start", t: "date" }, { k: "sailing_end", l: "Sailing end", t: "date" },
    { k: "cabin", l: "Cabin" }, { k: "category", l: "Cabin category" },
    { k: "confirmation", l: "Confirmation #" },
    { k: "image", l: "Ship image", t: "image" },
    { k: "notes", l: "Notes", t: "textarea" },
  ],
};

const MONTHS = { JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6, JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12 };

function timeFrac(t) {
  if (!t || !/^\d{1,2}:\d{2}/.test(t)) return 0;
  const [h, m] = t.split(":").map(Number);
  return (h * 60 + m) / 2000;
}

function isoKey(iso) {
  if (!iso || iso.length < 10) return null;
  return Number(iso.slice(5, 7)) * 100 + Number(iso.slice(8, 10));
}

// Comparable chronological key (month*100+day + time fraction); null = undated
function getBlockKey(b) {
  const d = b.data || {};
  if (b.type === "flight_segment") {
    const raw = d.segment?.date_raw;
    if (!raw || raw.length < 5) return null;
    const day = Number(raw.slice(0, 2));
    const mon = MONTHS[raw.slice(2, 5).toUpperCase()];
    if (!mon) return null;
    return mon * 100 + day + timeFrac(d.segment?.departure_time);
  }
  const map = { hotel: "check_in", car_hire: "pickup_date", insurance: "start_date", transfer: "date", tour: "date", cruise: "sailing_start", note: "date" };
  const key = map[b.type];
  if (!key) return null;
  const base = isoKey(d[key]);
  if (base == null) return null;
  return base + timeFrac(d.time);
}

function sortBlocksByDate(blocks) {
  const positions = [];
  const dated = [];
  blocks.forEach((b, i) => {
    if (getBlockKey(b) != null) { positions.push(i); dated.push(b); }
  });
  dated.sort((a, b) => getBlockKey(a) - getBlockKey(b));
  const out = [...blocks];
  positions.forEach((pos, idx) => { out[pos] = dated[idx]; });
  return out;
}

// Blank cards shown in the builder for the selected layout (agents just fill them in).
const SECTION_SCAFFOLD = {
  premium: ["passengers", "hotel", "transfer", "tour", "billing"],
};
const DEFAULT_SCAFFOLD = ["hotel", "transfer", "tour"];
const SCAFFOLDABLE = new Set([...Object.values(SECTION_SCAFFOLD).flat(), ...DEFAULT_SCAFFOLD]);

const blankData = (type) =>
  type === "passengers" ? { travelers: [{ name: "", seat: "", tickets: [] }], names: [""] }
    : type === "billing" ? { items: [{ description: "", qty: "", unit_price: "", total: "" }] }
      : {};

function blockIsEmpty(b) {
  const d = b.data || {};
  if (b.type === "passengers") {
    const tv = d.travelers || (d.names || []).map((n) => ({ name: n }));
    return !tv.some((t) => t && ([t.name, t.seat].some((v) => v && String(v).trim())
      || (t.eticket && String(t.eticket).trim())
      || (t.tickets || []).some((k) => k && (k.number || k.airline))));
  }
  if (b.type === "billing") {
    const hasCust = ["customer_name", "customer_phone", "customer_email", "customer_address", "taxes", "currency"]
      .some((k) => d[k] && String(d[k]).trim());
    const hasItem = (d.items || []).some((i) => i && (i.description || i.qty || i.unit_price || i.total));
    return !hasCust && !hasItem;
  }
  return !Object.values(d).some((v) => v && String(v).trim());
}

// Ensure the selected layout always shows its full set of (blank) cards, without
// deleting any card the agent already filled in. Empty scaffold cards are de-duped.
function scaffoldBlocks(blocks, theme) {
  const wanted = SECTION_SCAFFOLD[theme] || DEFAULT_SCAFFOLD;
  let kept = (blocks || []).filter((b) => !(SCAFFOLDABLE.has(b.type) && blockIsEmpty(b)));
  wanted.forEach((t) => {
    if (!kept.some((b) => b.type === t)) kept.push({ id: uid(), type: t, data: blankData(t) });
  });
  const contacts = kept.filter((b) => b.type === "agent_contact");
  const rest = kept.filter((b) => b.type !== "agent_contact");
  return [...rest, ...contacts];
}

function SortableBlock({ block, onChange, onRemove, onUpload }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const meta = BLOCK_LABELS[block.type] || {};
  const Icon = meta.icon || Type;
  const seg = block.data?.segment || {};

  return (
    <div ref={setNodeRef} style={style} data-testid={`block-${block.type}`} className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50 rounded-t-lg">
        <button {...attributes} {...listeners} className="cursor-grab text-slate-400 hover:text-slate-600" data-testid="drag-handle"><GripVertical className="h-4 w-4" /></button>
        <Icon className="h-4 w-4 text-blue-600" />
        <span className="text-sm font-medium text-slate-700">{meta.label}</span>
        <div className="flex-1" />
        <button onClick={() => onRemove(block.id)} className="text-slate-400 hover:text-red-500" data-testid="block-remove"><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="p-3">
        {block.type === "flight_segment" && (() => {
          const setSeg = (patch) => onChange(block.id, { segment: { ...seg, ...patch } });
          const FF = [
            { k: "airline_name", l: "Airline" }, { k: "airline_code", l: "Airline code", up: true, max: 4 }, { k: "flight_number", l: "Flight no." },
            { k: "origin_code", l: "From (code)", up: true, max: 4 }, { k: "origin_city", l: "From city" },
            { k: "destination_code", l: "To (code)", up: true, max: 4 }, { k: "destination_city", l: "To city" },
            { k: "date", l: "Departure date", kind: "date" }, { k: "departure_time", l: "Departure time", kind: "time" },
            { k: "arrival_date", l: "Arrival date", kind: "date" }, { k: "arrival_time", l: "Arrival time", kind: "time" },
            { k: "cabin_class", l: "Cabin" }, { k: "booking_class", l: "Booking class" }, { k: "status", l: "Status" },
            { k: "seat", l: "Seat(s)" }, { k: "terminal", l: "Terminal" }, { k: "eticket", l: "E-ticket(s)" },
            { k: "carry_on_baggage", l: "Carry-on baggage" }, { k: "checkin_baggage", l: "Check-in baggage" },
            { k: "frequent_flyer", l: "Frequent flyer" }, { k: "meal", l: "Meal preference" },
            { k: "record_locator", l: "Record locator" },
          ];
          return (
            <div className="text-sm space-y-2" data-testid="flight-editor">
              <div className="font-medium text-slate-900">{seg.airline_name} {seg.airline_code}{seg.flight_number}</div>
              <div className="text-slate-500 font-pnr text-xs">{seg.origin_code} {seg.departure_time} → {seg.destination_code} {seg.arrival_time} · {seg.date} · {seg.status}</div>
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
                {FF.map((f) => {
                  if (f.k === "airline_name") {
                    return (
                      <div key={f.k}>
                        <label className="text-xs text-slate-500">{f.l}</label>
                        <AirlineAutocomplete
                          value={seg.airline_name || ""}
                          onChange={(v) => setSeg({ airline_name: v, custom_airline: true })}
                          onSelect={(a) => setSeg({ airline_name: a.name, airline_code: a.code, custom_airline: true })}
                          placeholder="Search airline…"
                          className="mt-0.5"
                          testid="flight-airline_name"
                        />
                      </div>
                    );
                  }
                  if (f.k === "origin_code" || f.k === "destination_code") {
                    const isOrigin = f.k === "origin_code";
                    const cityKey = isOrigin ? "origin_city" : "destination_city";
                    return (
                      <div key={f.k}>
                        <label className="text-xs text-slate-500">{f.l}</label>
                        <AirportAutocomplete
                          value={seg[f.k] || ""}
                          onChange={(v) => setSeg({ [f.k]: v })}
                          onCodeResolved={(a) => setSeg({ [f.k]: a.code, [cityKey]: a.city })}
                          onSelect={(a) => setSeg({ [f.k]: a.code, [cityKey]: a.city })}
                          placeholder="Code or city…"
                          className="mt-0.5 font-pnr uppercase"
                          testid={`flight-${f.k}`}
                        />
                      </div>
                    );
                  }
                  if (f.kind === "date") {
                    return (
                      <div key={f.k}>
                        <label className="text-xs text-slate-500">{f.l}</label>
                        <DatePickerField
                          value={seg[f.k] || ""}
                          onChange={(v) => setSeg({ [f.k]: v })}
                          testid={`flight-${f.k}`}
                        />
                      </div>
                    );
                  }
                  if (f.kind === "time") {
                    return (
                      <div key={f.k}>
                        <label className="text-xs text-slate-500">{f.l}</label>
                        <TimePickerField
                          value={seg[f.k] || ""}
                          onChange={(v) => setSeg({ [f.k]: v })}
                          testid={`flight-${f.k}`}
                        />
                      </div>
                    );
                  }
                  return (
                    <div key={f.k}>
                      <label className="text-xs text-slate-500">{f.l}</label>
                      <Input value={seg[f.k] || ""} maxLength={f.max}
                        onChange={(e) => setSeg({ [f.k]: f.up ? e.target.value.toUpperCase() : e.target.value, ...((f.k === "airline_code" || f.k === "airline_name") ? { custom_airline: true } : {}) })}
                        className={`mt-0.5 ${f.up ? "font-pnr uppercase" : ""}`} data-testid={`flight-${f.k}`} />
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400">Enter multiple seats or e-tickets separated by comma, semicolon or space — each shows on its own line. Airline name/code overrides apply for private or charter flights.</p>
            </div>
          );
        })()}
        {block.type === "text" && (
          <div className="space-y-2">
            <Input value={block.data.heading || ""} onChange={(e) => onChange(block.id, { heading: e.target.value })} placeholder="Heading (optional)" data-testid="text-heading" />
            <Textarea value={block.data.body || ""} onChange={(e) => onChange(block.id, { body: e.target.value })} placeholder="Write something for your client…" className="min-h-[80px]" data-testid="text-body" />
          </div>
        )}
        {block.type === "image" && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input value={block.data.url || ""} onChange={(e) => onChange(block.id, { url: e.target.value })} placeholder="Image URL" className="font-pnr text-xs" data-testid="image-url" />
              <Button type="button" variant="outline" size="sm" className="border-slate-200 shrink-0" onClick={() => onUpload(block.id)}><Upload className="h-4 w-4" /></Button>
            </div>
            {block.data.url && <img src={block.data.url} alt={block.data.caption || "Itinerary image"} className="rounded-md max-h-40 object-cover w-full" />}
            <Input value={block.data.caption || ""} onChange={(e) => onChange(block.id, { caption: e.target.value })} placeholder="Caption (optional)" />
          </div>
        )}
        {block.type === "divider" && <div className="text-xs text-slate-400">A horizontal divider line.</div>}
        {block.type === "agent_contact" && <div className="text-xs text-slate-400">Shows your agency name, agent details and logo from Branding settings.</div>}
        {block.type === "passengers" && (() => {
          const d = block.data || {};
          const norm = (t) => {
            let tickets = Array.isArray(t.tickets) ? t.tickets : [];
            if (!tickets.length && t.eticket) {
              tickets = String(t.eticket).split(/[,;\s]+/).filter(Boolean).map((n) => ({ airline: "", number: n }));
            }
            return { name: t.name || "", seat: t.seat || "", type: t.type, frequentFlyer: t.frequentFlyer, tickets };
          };
          const travelers = d.travelers && d.travelers.length
            ? d.travelers.map(norm)
            : (d.names && d.names.length ? d.names.map((n) => ({ name: n, seat: "", tickets: [] })) : [{ name: "", seat: "", tickets: [] }]);
          const save = (arr) => onChange(block.id, {
            travelers: arr.map((t) => ({ ...t, eticket: (t.tickets || []).map((k) => k.number).filter(Boolean).join(", ") })),
            names: arr.map((t) => t.name || ""),
          });
          const setRow = (i, patch) => save(travelers.map((t, x) => (x === i ? { ...t, ...patch } : t)));
          const removeRow = (i) => { const a = travelers.filter((_, x) => x !== i); save(a.length ? a : [{ name: "", seat: "", tickets: [] }]); };
          const setTicket = (i, j, patch) => setRow(i, { tickets: (travelers[i].tickets || []).map((k, y) => (y === j ? { ...k, ...patch } : k)) });
          const addTicket = (i) => setRow(i, { tickets: [...(travelers[i].tickets || []), { airline: "", number: "" }] });
          const removeTicket = (i, j) => setRow(i, { tickets: (travelers[i].tickets || []).filter((_, y) => y !== j) });
          return (
            <div className="space-y-3" data-testid="passengers-editor">
              <p className="text-xs text-slate-400">Each traveller's name, seat and e-ticket(s). Add one ticket per airline — it shows on that airline's flight card.</p>
              {travelers.map((t, i) => (
                <div key={`pax-${i}`} className="rounded-md border border-slate-100 p-2 space-y-2">
                  <div className="flex flex-wrap gap-2 items-center">
                    <Input className="flex-1 min-w-[160px]" value={t.name || ""} onChange={(e) => setRow(i, { name: e.target.value })} placeholder="Passenger name" data-testid={`passenger-name-${i}`} />
                    <Input className="w-24" value={t.seat || ""} onChange={(e) => setRow(i, { seat: e.target.value })} placeholder="Seat" data-testid={`passenger-seat-${i}`} />
                    <Button type="button" variant="outline" size="sm" className="border-slate-200 shrink-0" onClick={() => removeRow(i)} data-testid={`remove-passenger-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <div className="space-y-1.5">
                    {(t.tickets || []).map((k, j) => (
                      <div key={`tk-${i}-${j}`} className="flex gap-2 items-center">
                        <Input className="w-20 font-pnr uppercase" maxLength={3} value={k.airline || ""} onChange={(e) => setTicket(i, j, { airline: e.target.value.toUpperCase() })} placeholder="Airline" data-testid={`passenger-${i}-ticket-airline-${j}`} />
                        <Input className="flex-1 font-pnr" value={k.number || ""} onChange={(e) => setTicket(i, j, { number: e.target.value })} placeholder="E-ticket number" data-testid={`passenger-${i}-ticket-number-${j}`} />
                        <button type="button" className="text-slate-400 hover:text-red-500 shrink-0" onClick={() => removeTicket(i, j)} data-testid={`passenger-${i}-remove-ticket-${j}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                    <Button type="button" variant="ghost" size="sm" className="text-blue-600 h-7 px-2" onClick={() => addTicket(i)} data-testid={`passenger-${i}-add-ticket`}><Plus className="h-3.5 w-3.5 mr-1" /> Add ticket</Button>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" className="border-slate-200" onClick={() => save([...travelers, { name: "", seat: "", tickets: [] }])} data-testid="add-passenger-name"><Plus className="h-3.5 w-3.5 mr-1" /> Add passenger</Button>
            </div>
          );
        })()}
        {block.type === "billing" && (() => {
          const d = block.data || {};
          const items = d.items && d.items.length ? d.items : [{ description: "", qty: "", unit_price: "", total: "" }];
          const setItems = (arr) => onChange(block.id, { items: arr });
          const setItem = (i, patch) => setItems(items.map((it, x) => (x === i ? { ...it, ...patch } : it)));
          const F = [["customer_name", "Customer name"], ["customer_phone", "Phone"], ["customer_email", "Email"], ["customer_address", "Billing address"]];
          return (
            <div className="space-y-3" data-testid="billing-editor">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-500">Currency</label>
                  <Input value={d.currency || ""} onChange={(e) => onChange(block.id, { currency: e.target.value })} placeholder="$ / £ / USD" className="mt-0.5" data-testid="field-billing-currency" />
                </div>
                {F.map(([k, l]) => (
                  <div key={k} className={k === "customer_address" ? "col-span-2" : ""}>
                    <label className="text-xs text-slate-500">{l}</label>
                    <Input value={d[k] || ""} onChange={(e) => onChange(block.id, { [k]: e.target.value })} className="mt-0.5" data-testid={`field-billing-${k}`} />
                  </div>
                ))}
              </div>
              <div className="pt-2 border-t border-slate-100">
                <label className="text-xs text-slate-500">Line items</label>
                {items.map((it, i) => (
                  <div key={`bill-${i}`} className="flex gap-1.5 mt-1.5 items-center">
                    <Input className="flex-[3]" placeholder="Description" value={it.description || ""} onChange={(e) => setItem(i, { description: e.target.value })} data-testid={`billing-item-desc-${i}`} />
                    <Input className="flex-1" placeholder="Qty" value={it.qty || ""} onChange={(e) => setItem(i, { qty: e.target.value })} data-testid={`billing-item-qty-${i}`} />
                    <Input className="flex-1" placeholder="Unit" value={it.unit_price || ""} onChange={(e) => setItem(i, { unit_price: e.target.value })} data-testid={`billing-item-unit-${i}`} />
                    <Input className="flex-1" placeholder="Total" value={it.total || ""} onChange={(e) => setItem(i, { total: e.target.value })} data-testid={`billing-item-total-${i}`} />
                    <button type="button" className="text-slate-400 hover:text-red-500 shrink-0" onClick={() => { const r = items.filter((_, x) => x !== i); setItems(r.length ? r : [{ description: "", qty: "", unit_price: "", total: "" }]); }} data-testid={`remove-billing-item-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" className="border-slate-200 mt-2" onClick={() => setItems([...items, { description: "", qty: "", unit_price: "", total: "" }])} data-testid="add-billing-item"><Plus className="h-3.5 w-3.5 mr-1" /> Add line item</Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-500">Taxes (optional)</label>
                  <Input value={d.taxes || ""} onChange={(e) => onChange(block.id, { taxes: e.target.value })} className="mt-0.5" data-testid="field-billing-taxes" />
                </div>
              </div>
              <p className="text-xs text-slate-400">Subtotal and total are calculated automatically from the line items.</p>
            </div>
          );
        })()}
        {FORM_FIELDS[block.type] && (
          <div className="grid grid-cols-2 gap-2">
            {FORM_FIELDS[block.type].map((f) => (
              <div key={f.k} className={f.t === "textarea" || f.t === "image" ? "col-span-2" : ""}>
                <label className="text-xs text-slate-500">{f.l}</label>
                {f.t === "textarea" ? (
                  <Textarea value={block.data[f.k] || ""} onChange={(e) => onChange(block.id, { [f.k]: e.target.value })} className="mt-0.5 min-h-[60px]" data-testid={`field-${block.type}-${f.k}`} />
                ) : f.t === "image" ? (
                  <div className="mt-0.5 space-y-1.5">
                    <div className="flex gap-2">
                      <Input value={block.data[f.k] || ""} onChange={(e) => onChange(block.id, { [f.k]: e.target.value })} placeholder="Image URL" className="font-pnr text-xs" data-testid={`field-${block.type}-${f.k}`} />
                      <Button type="button" variant="outline" size="sm" className="border-slate-200 shrink-0" onClick={() => onUpload({ id: block.id, key: f.k })} data-testid={`upload-${block.type}-${f.k}`}><Upload className="h-4 w-4" /></Button>
                    </div>
                    {block.data[f.k] && <img src={block.data[f.k]} alt={f.l} className="rounded-md max-h-32 object-cover w-full" />}
                  </div>
                ) : (
                  <Input type={f.t || "text"} value={block.data[f.k] || ""} onChange={(e) => onChange(block.id, { [f.k]: e.target.value })} className="mt-0.5" data-testid={`field-${block.type}-${f.k}`} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ItineraryBuilder() {
  const { id } = useParams();
  const [it, setIt] = useState(null);
  const [themes, setThemes] = useState([]);
  const [feats, setFeats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [shareEnabled, setShareEnabled] = useState(false);
  const [sharePassword, setSharePassword] = useState("");
  const [shareId, setShareId] = useState(null);
  const uploadTarget = useRef(null);
  const fileRef = useRef();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const loadPreview = useCallback(async () => {
    if (!it) return;
    try {
      const { data } = await api.post(`/itineraries/preview.html`, {
        title: it.title, theme: it.theme, layout: it.layout, gds: it.gds,
        segments: it.segments || [], blocks: it.blocks || [],
        extras: it.extras || {}, destination_city: it.destination_city, booking_reference: it.booking_reference,
        destination_image_url: it.destination_image_url, display_settings: it.display_settings || {},
      }, { responseType: "text" });
      setPreviewHtml(data);
    } catch (e) { console.debug("preview load failed", e); }
  }, [it]);

  useEffect(() => {
    if (!it) return;
    const h = setTimeout(loadPreview, 500);
    return () => clearTimeout(h);
  }, [it, loadPreview]);

  useEffect(() => {
    (async () => {
      try {
        const [i, t, b] = await Promise.all([api.get(`/itineraries/${id}`), api.get(`/themes`), api.get(`/billing/me`).catch(() => null)]);
        setIt({ ...i.data, blocks: scaffoldBlocks(i.data.blocks || [], i.data.theme || i.data.layout) });
        setThemes(t.data);
        if (b?.data?.features) setFeats(b.data.features);
        setShareEnabled(i.data.share?.enabled || false);
        setShareId(i.data.share?.share_id || null);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const changeBlock = (bid, patch) => setIt((p) => ({ ...p, blocks: p.blocks.map((b) => b.id === bid ? { ...b, data: { ...b.data, ...patch } } : b) }));
  const removeBlock = (bid) => setIt((p) => ({ ...p, blocks: p.blocks.filter((b) => b.id !== bid) }));
  const addBlock = (type) => setIt((p) => ({ ...p, blocks: [...p.blocks, { id: uid(), type, data: {} }] }));

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setIt((p) => {
      const oldI = p.blocks.findIndex((b) => b.id === active.id);
      const newI = p.blocks.findIndex((b) => b.id === over.id);
      return { ...p, blocks: arrayMove(p.blocks, oldI, newI) };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/itineraries/${id}`, {
        title: it.title, theme: it.theme, blocks: it.blocks,
        destination_city: it.destination_city, destination_image_url: it.destination_image_url,
        booking_reference: it.booking_reference,
        display_settings: it.display_settings || {},
      });
      setIt((p) => ({ ...p, ...data }));
      toast.success("Saved.");
      loadPreview();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${BACKEND}${data.url}`;
      const t = uploadTarget.current;
      if (t === "destination") setIt((p) => ({ ...p, destination_image_url: url }));
      else if (t && typeof t === "object") changeBlock(t.id, { [t.key]: url });
      else if (t) changeBlock(t, { url });
      toast.success("Image uploaded.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    e.target.value = "";
  };
  const triggerUpload = (target) => { uploadTarget.current = target; fileRef.current?.click(); };

  const download = async (path, filename, type) => {
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type }));
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed."); }
  };

  const whatsapp = async () => {
    let text;
    try {
      const { data } = await api.get(`/itineraries/${id}/whatsapp.txt`);
      text = data.text;
    } catch (e) {
      toast.error("Failed to build WhatsApp text.");
      return;
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Text copied & WhatsApp opened.");
    } catch (e) {
      toast.success("WhatsApp opened with your itinerary text.");
    }
  };

  const saveShare = async () => {
    try {
      const { data } = await api.put(`/itineraries/${id}/share`, { enabled: shareEnabled, password: sharePassword || undefined });
      setShareId(data.share_id);
      setShareEnabled(data.enabled);
      toast.success(data.enabled ? "Share link is live." : "Sharing disabled.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const shareUrl = shareId ? `${window.location.origin}/share/${shareId}` : "";

  if (!it) return <div className="min-h-screen bg-slate-50"><Navbar /><div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={doUpload} data-testid="builder-file-input" />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <Input value={it.title} onChange={(e) => setIt((p) => ({ ...p, title: e.target.value }))} className="max-w-xs font-heading font-bold text-lg" data-testid="builder-title" />
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="border-slate-200" onClick={() => download(`/itineraries/${id}/export.pdf`, "itinerary.pdf", "application/pdf")} data-testid="export-pdf-btn"><FileDown className="h-4 w-4 mr-1.5" /> PDF</Button>
            <Button variant="outline" size="sm" className="border-slate-200" disabled={feats && !feats.exports?.includes("html")} onClick={() => download(`/itineraries/${id}/export.html`, "itinerary.html", "text/html")} data-testid="export-html-btn"><Code className="h-4 w-4 mr-1.5" /> HTML</Button>
            <Button variant="outline" size="sm" className="border-slate-200" disabled={feats && !feats.exports?.includes("whatsapp")} onClick={whatsapp} data-testid="export-wa-text-btn"><MessageCircle className="h-4 w-4 mr-1.5" /> WhatsApp</Button>
            <Button variant="outline" size="sm" className="border-slate-200" disabled={feats && !feats.exports?.includes("whatsapp")} onClick={() => download(`/itineraries/${id}/whatsapp.png`, "itinerary.png", "image/png")} data-testid="export-wa-img-btn"><ImageIcon className="h-4 w-4 mr-1.5" /> Image</Button>
            <Button variant="outline" size="sm" className="border-slate-200" disabled={feats && !feats.share} onClick={() => setShareOpen(true)} data-testid="share-btn"><Share2 className="h-4 w-4 mr-1.5" /> Share</Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save} disabled={saving} data-testid="builder-save"><Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save"}</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:h-[calc(100vh-11rem)]">
          {/* Editor */}
          <div className="lg:h-full lg:overflow-y-auto lg:pr-2">
            <div className="rounded-lg border border-slate-200 bg-white p-4 mb-4">
              <Label className="text-slate-700">Layout</Label>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="theme-picker">
                {themes.map((t) => {
                  const locked = feats && feats.allowed_layouts !== "all" && !feats.allowed_layouts.includes(t.id);
                  return (
                  <button key={t.id} data-testid={`theme-${t.id}`} disabled={locked} onClick={() => setIt((p) => ({ ...p, theme: t.id, blocks: scaffoldBlocks(p.blocks, t.id) }))}
                    className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${locked ? "border-slate-200 text-slate-300 cursor-not-allowed" : it.theme === t.id ? "border-blue-500 bg-blue-50 text-blue-700 font-medium" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                    {t.name}{locked ? " 🔒" : ""}
                  </button>
                  );
                })}
              </div>
              <div className="mt-4 grid grid-cols-1 gap-2">
                <Label className="text-slate-700">Booking reference <span className="text-xs text-slate-400 font-normal">(your agency booking number — not the airline PNR)</span></Label>
                <Input value={it.booking_reference || ""} onChange={(e) => setIt((p) => ({ ...p, booking_reference: e.target.value }))} placeholder="e.g. ABC-2026-00123" className="font-pnr" data-testid="booking-reference" />
              </div>
              <div className="mt-4 grid grid-cols-1 gap-2">
                <Label className="text-slate-700">Destination</Label>
                <Input value={it.destination_city || ""} onChange={(e) => setIt((p) => ({ ...p, destination_city: e.target.value }))} placeholder="Destination city" data-testid="dest-city" />
                <div className="flex gap-2">
                  <Input value={it.destination_image_url || ""} onChange={(e) => setIt((p) => ({ ...p, destination_image_url: e.target.value }))} placeholder="Destination image URL" className="font-pnr text-xs" data-testid="dest-image-url" />
                  <Button type="button" variant="outline" size="sm" className="border-slate-200 shrink-0" onClick={() => triggerUpload("destination")} data-testid="dest-image-upload"><Upload className="h-4 w-4" /></Button>
                </div>
                {it.destination_image_url && <img src={it.destination_image_url} alt={`${it.destination || "Destination"} photo`} className="rounded-md h-32 w-full object-cover" />}
              </div>
            </div>

            <div className="mb-3 space-y-2" data-testid="add-block-bar">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-slate-500 mr-1">Add item:</span>
                <Button variant="outline" size="sm" className="border-slate-200" onClick={() => addBlock("flight_segment")} data-testid="add-flight_segment"><Plane className="h-3.5 w-3.5 mr-1" /> Flight</Button>
                {ITEM_TYPES.map((t) => {
                  const M = BLOCK_LABELS[t];
                  const Icon = M.icon;
                  return <Button key={t} variant="outline" size="sm" className="border-slate-200" onClick={() => addBlock(t)} data-testid={`add-${t}`}><Icon className="h-3.5 w-3.5 mr-1" /> {M.label}</Button>;
                })}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-slate-500 mr-1">Add section:</span>
                {SECTION_TYPES.map((t) => {
                  const M = BLOCK_LABELS[t];
                  const Icon = M.icon;
                  return <Button key={t} variant="outline" size="sm" className="border-slate-200" onClick={() => addBlock(t)} data-testid={`add-${t}`}><Icon className="h-3.5 w-3.5 mr-1" /> {M.label}</Button>;
                })}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-slate-500 mr-1">Layout:</span>
                {LAYOUT_TYPES.map((t) => {
                  const M = BLOCK_LABELS[t];
                  return <Button key={t} variant="outline" size="sm" className="border-slate-200" onClick={() => addBlock(t)} data-testid={`add-${t}`}><Plus className="h-3.5 w-3.5 mr-1" /> {M.label}</Button>;
                })}
                <Button variant="outline" size="sm" className="border-slate-200 text-blue-700" onClick={() => setIt((p) => ({ ...p, blocks: sortBlocksByDate(p.blocks) }))} data-testid="sort-by-date-btn">
                  <CalendarClock className="h-3.5 w-3.5 mr-1" /> Sort by date
                </Button>
              </div>
            </div>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={it.blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-3">
                  {it.blocks.map((b) => (
                    <SortableBlock key={b.id} block={b} onChange={changeBlock} onRemove={removeBlock} onUpload={triggerUpload} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          {/* Preview */}
          <div className="flex flex-col lg:h-full">
            <div className="flex items-center gap-2 mb-2 text-sm text-slate-500 shrink-0"><Eye className="h-4 w-4" /> Live preview <span className="text-xs text-slate-400">(updates as you edit)</span></div>
            <iframe title="preview" data-testid="builder-preview" srcDoc={previewHtml} className="w-full flex-1 min-h-[720px] lg:min-h-0 rounded-lg border border-slate-200 bg-white" />
          </div>
        </div>
      </main>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Share itinerary</DialogTitle>
            <DialogDescription>Create a password-protected public link for your client.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Enable public link</Label>
              <Switch checked={shareEnabled} onCheckedChange={setShareEnabled} data-testid="share-enable" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="text" value={sharePassword} onChange={(e) => setSharePassword(e.target.value)} placeholder={shareId ? "Set a new password (leave blank to keep)" : "Set a password"} className="mt-1" data-testid="share-password" />
            </div>
            {shareEnabled && shareId && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs text-slate-500 mb-1">Share link</div>
                <div className="flex gap-2">
                  <code className="flex-1 font-pnr text-xs truncate" data-testid="share-url">{shareUrl}</code>
                  <Button size="sm" variant="outline" className="border-slate-200" onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied."); }}>Copy</Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareOpen(false)} className="border-slate-200">Close</Button>
            <Button onClick={saveShare} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="share-save">Save sharing</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
